const SWYM_WAIT_MS = 8000;
const wishlistEnabled = !document.documentElement.classList.contains('bt-wishlist-off');
const BUTTONS = '.product-card__wishlist, [data-bt-pdp-wishlist], [data-wishlist]';
const CONTEXT = 'product-card, [data-stl-product], [data-quick-add-drawer-content], bt-product-information';

let apiPromise = null;
let itemsPromise = null;
/** @type {Set<string> | null} */
let keys = null;
let bound = false;
/** @type {MutationObserver | null} */
let observer = null;
let paintQueued = false;
/** @type {Set<Element>} */
const pendingPaint = new Set();

function resolveApi() {
  if (apiPromise) return apiPromise;

  const pending = new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value);
    };

    const timer = window.setTimeout(() => done(null), SWYM_WAIT_MS);

    if (window._swat) {
      done(window._swat);
      return;
    }

    try {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => done(swat || window._swat || null));
    } catch {
      done(null);
    }
  });

  apiPromise = pending.then((swat) => {
    if (!swat) apiPromise = null;
    return swat;
  });
  return apiPromise;
}

function numericId(value) {
  const number = Number(value);
  if (Number.isFinite(number) && number > 0) return number;

  const match = String(value || '').match(/(\d+)$/);
  const parsed = Number(match?.[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : NaN;
}

export function toProduct({ productId, variantId, url }) {
  const empi = numericId(productId);
  const epi = numericId(variantId);
  if (!Number.isFinite(empi)) return null;

  const product = { empi };
  if (Number.isFinite(epi)) product.epi = epi;
  if (url) {
    try {
      product.du = new URL(url, window.location.origin).href.split('?')[0];
    } catch {
      // Swym accepts products without a detail URL.
    }
  }
  return product;
}

export function productKey(product) {
  return `${product?.empi ?? ''}:${product?.epi ?? ''}`;
}

function settleWithTimeout(run) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    window.setTimeout(() => done(null), SWYM_WAIT_MS);
    try {
      run(done);
    } catch {
      done(null);
    }
  });
}

function normalizeItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.listcontents)) return value.listcontents;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

export async function isAvailable() {
  return Boolean(await resolveApi());
}

async function loadWishlistItems() {
  const swat = await resolveApi();
  if (!swat) return null;

  if (typeof swat.fetchLists === 'function') {
    const lists = await settleWithTimeout((done) => {
      swat.fetchLists({
        callbackFn: (list) => done(list),
        errorFn: () => done(null),
      });
    });
    if (lists != null) {
      if (Array.isArray(lists)) {
        const fromLists = lists.flatMap((list) => (Array.isArray(list?.listcontents) ? list.listcontents : []));
        if (fromLists.length || lists.some((list) => Array.isArray(list?.listcontents))) {
          return fromLists;
        }
        return lists;
      }
      return normalizeItems(lists);
    }
  }

  if (typeof swat.fetch !== 'function') return null;

  const items = await settleWithTimeout((done) => {
    swat.fetch(
      (list) => done(list),
      () => done(null)
    );
  });

  return items == null ? null : normalizeItems(items);
}

export async function wishlistItems() {
  if (!wishlistEnabled) return [];
  if (!itemsPromise) {
    itemsPromise = loadWishlistItems().then((items) => {
      if (items == null) itemsPromise = null;
      return items;
    });
  }
  return itemsPromise;
}

export async function wishlistKeys() {
  const items = await wishlistItems();
  if (!items) return null;

  const next = new Set();
  for (const item of items) {
    next.add(productKey({ empi: Number(item.empi), epi: Number(item.epi) }));
    next.add(`${Number(item.empi)}:`);
  }
  return next;
}

async function persist(product, wanted) {
  const swat = await resolveApi();
  if (!swat || !product) return null;

  const method = wanted ? swat.addToWishList : swat.removeFromWishList;
  if (typeof method !== 'function') return null;

  return settleWithTimeout((done) => {
    method.call(
      swat,
      product,
      () => done(wanted),
      () => done(null)
    );
  });
}

/**
 * Add or remove a product in Swym. On success every heart for that product
 * repaints and `wishlist:state` is dispatched.
 * @returns {Promise<boolean | null>} The new state, or null when Swym failed.
 */
export async function toggle(product, wanted) {
  if (!wishlistEnabled) return null;
  const result = await persist(product, wanted);
  if (result === null) return null;

  itemsPromise = null;
  remember(product, wanted);
  paint(document.querySelectorAll(BUTTONS));
  broadcast(product, wanted);
  return result;
}

/** @param {Element | null} root */
function variantFromContext(root) {
  if (!(root instanceof HTMLElement)) return '';
  if (root.dataset.variantId) return root.dataset.variantId;

  const selected = root.querySelector('input[name="id"]');
  if (selected instanceof HTMLInputElement && selected.value) return selected.value;

  const marked = root.querySelector('[data-variant-id]:not([data-available="false"])');
  if (marked instanceof HTMLElement && marked.dataset.variantId) return marked.dataset.variantId;

  const script = root.querySelector('script[data-product-variants]');
  if (!(script instanceof HTMLScriptElement)) return '';

  try {
    const variants = JSON.parse(script.textContent || '[]');
    if (!Array.isArray(variants)) return '';
    const match = variants.find((variant) => variant?.available && variant.id) || variants.find((variant) => variant?.id);
    return match?.id != null ? String(match.id) : '';
  } catch {
    return '';
  }
}

/** @param {Element | null} root */
function urlFromContext(root) {
  if (!(root instanceof Element)) return '';
  const link = root.querySelector('[ref="productCardLink"][href], a.product-card__title[href], a[href*="/products/"]');
  return link instanceof HTMLAnchorElement ? link.href : '';
}

/** @param {HTMLElement | null} button */
function productIdOf(button) {
  return button?.dataset.productId || button?.closest('[data-product-id]')?.getAttribute('data-product-id') || '';
}

/**
 * @param {HTMLElement | null} button
 * @param {{ productId?: string, variantId?: string, url?: string }} detail
 */
function productFromButton(button, detail) {
  const context = button?.closest(CONTEXT) ?? null;
  return toProduct({
    productId: detail.productId || productIdOf(button),
    variantId: detail.variantId || button?.dataset.variantId || variantFromContext(context),
    url: detail.url || urlFromContext(context),
  });
}

/**
 * @param {HTMLElement} button
 * @param {boolean} inList
 */
function setButtonState(button, inList) {
  button.setAttribute('aria-pressed', inList ? 'true' : 'false');
  button.classList.toggle('is-active', inList);
  const label = inList ? button.dataset.labelRemove : button.dataset.labelAdd;
  if (label) button.setAttribute('aria-label', label);
}

function remember(product, wanted) {
  if (!keys) keys = new Set();
  const exact = productKey(product);
  const loose = `${product.empi}:`;
  if (wanted) {
    keys.add(exact);
    keys.add(loose);
  } else {
    keys.delete(exact);
    keys.delete(loose);
  }
  observe();
}

/** @param {Iterable<Element>} buttons */
function paint(buttons) {
  if (!keys) return;
  for (const button of buttons) {
    if (!(button instanceof HTMLElement) || button.dataset.busy === 'true') continue;
    const empi = numericId(productIdOf(button));
    if (!Number.isFinite(empi)) continue;
    setButtonState(button, keys.has(`${empi}:`));
  }
}

/** @param {Element} button */
function queuePaint(button) {
  pendingPaint.add(button);
  if (paintQueued) return;
  paintQueued = true;
  requestAnimationFrame(() => {
    paintQueued = false;
    const buttons = [...pendingPaint];
    pendingPaint.clear();
    paint(buttons);
  });
}

/** Paints hearts on cards rendered after load (Nosto grids, infinite scroll, drawers). */
function observe() {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(BUTTONS)) queuePaint(node);
        for (const button of node.querySelectorAll(BUTTONS)) queuePaint(button);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

function broadcast(product, active) {
  document.dispatchEvent(
    new CustomEvent('wishlist:state', {
      detail: { productId: String(product.empi), variantId: product.epi ? String(product.epi) : '', active },
    })
  );
}

/**
 * @param {HTMLElement | null} button
 * @param {{ productId?: string, variantId?: string, url?: string, active?: boolean }} detail
 */
async function runToggle(button, detail) {
  if (button?.dataset.busy === 'true') return;
  const product = productFromButton(button, detail);
  if (!product) return;

  const wanted = typeof detail.active === 'boolean' ? detail.active : button?.getAttribute('aria-pressed') !== 'true';

  if (button) {
    button.dataset.busy = 'true';
    setButtonState(button, wanted);
  }

  const result = await toggle(product, wanted);

  if (button) delete button.dataset.busy;
  if (result !== null) return;

  if (button) setButtonState(button, !wanted);
  broadcast(product, !wanted);
}

/** @param {Event} event */
function onToggle(event) {
  const detail = event instanceof CustomEvent && event.detail && typeof event.detail === 'object' ? event.detail : {};
  const source = detail.button ?? event.target;
  const button = source instanceof Element ? source.closest(BUTTONS) : null;
  void runToggle(button instanceof HTMLElement ? button : null, detail);
}

function bindWishlist() {
  if (!wishlistEnabled || bound) return;
  bound = true;

  document.addEventListener('wishlist:toggle', onToggle);

  void wishlistKeys().then((next) => {
    if (!next) return;
    keys = keys ? new Set([...next, ...keys]) : next;
    observe();
    paint(document.querySelectorAll(BUTTONS));
  });
}

bindWishlist();
